import { Box, FormControlLabel, Switch } from '@material-ui/core';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FBApproversProps, FBAutocompleteAsync, FBAutocompleteAsyncOption, FBInput, FBMapWrapper, FBRequiredApprover } from '..';
import { authSelectors } from '../../../state/ducks/auth';
import { changeRequestsActions } from '../../../state/ducks/changeRequest';
import ApproverConfigurationDialog from '../../administration/general.settings/components/configurationDialog/ApproverConfigurationDialog';
import useStyles from '../../administration/general.settings/components/configurationDialog/dialog.styles';
import { evaluateRequirements } from '../../administration/general.settings/panels/ApprovalConfigurations/helpers';
import AlertDialog from '../../app/alert.dialog/AlertDialog';
import Approvers from '../../change.request/summary/approvers';
import ApprovalWorkflow from '../../components/common/approval.workflow/ApprovalWorkflow';
import { Button } from '../../components/forms/fields-next';
import { toastError } from '../../components/notifications';
import useActionCreator from '../../hooks/useActionCreator';
import useAsync from '../../hooks/useAsync';
import useDialog from '../../hooks/useDialog';
import ApprovalApprovedGroup from '../FBApprovals/ApprovalApprovedGroup';
import { withFBApprovers } from './FBApprovers.wrap';

const FBApprovers: React.FunctionComponent<FBApproversProps> = ({
  renderOption,
  isIncluded,
  onBlur,
  name,
  gutter,
  disabled,
  loading,
  defaultValue,
  groups = {},
  autosave,
  changeRequestApprovals,
  approvalApprovedGroup,
  changeRequestId,
  isApprovalEditEnabled,
  approvalStages,
  workflowStatusDetails,
}) => {
  const classes = useStyles();
  const [isAdvancedEnabled, setIsAdvancedEnabled] = useState<boolean>(approvalStages.length > 0);
  const isApproverSelectionDisabled = disabled || isAdvancedEnabled;

  const loadChangeRequestAction = useActionCreator(changeRequestsActions.load);

  const currentEmployeeId = useSelector(authSelectors.currentEmployeeId);

  const configureDialog = useDialog();

  const checkedGroups = useMemo(
    () => evaluateRequirements(approvalStages, groups),
    [approvalStages, groups],
  );

  const unlinkConnectedApprovalsAction = useActionCreator(changeRequestsActions.unlinkConnectedApprovals);
  const unlinkConnectedApprovalsAsync = useAsync({
    onError: (error) => {
      toastError(error);
    },
    onSuccess: () => {
      loadChangeRequestAction(changeRequestId);
    },
  });
  function unlinkConnectedApprovals () {
    unlinkConnectedApprovalsAsync.start(
      unlinkConnectedApprovalsAction,
      changeRequestId,
      unlinkConnectedApprovalsAsync,
    );
  }

  const enableAdvancedDialog = useDialog();
  const cancelEnableAdvancedDialog = () => {
    enableAdvancedDialog.close();
  };
  const onConfirmEnableAdvanced = () => {
    setIsAdvancedEnabled(true);
    unlinkConnectedApprovals();
    enableAdvancedDialog.close();
  };

  const disableAdvancedDialog = useDialog();
  const cancelDisableAdvancedDialog = () => {
    disableAdvancedDialog.close();
  };
  const onConfirmDisableAdvanced = () => {
    setIsAdvancedEnabled(false);
    unlinkConnectedApprovals();
    disableAdvancedDialog.close();
  };

  const toggleAdvancedConfiguration = (isAdvancedChecked: boolean) => {
    if (!isApprovalEditEnabled) {
      return;
    }
    if (isAdvancedChecked) {
      if (changeRequestApprovals?.length) {
        enableAdvancedDialog.open();
      } else {
        setIsAdvancedEnabled(true);
      }
    }

    if (!isAdvancedChecked) {
      disableAdvancedDialog.open();
    }
  };

  const handleConfirm = () => {
    loadChangeRequestAction(changeRequestId);
  };

  return (
    <>
      <FBInput {...{ name, gutter, disabled, autosave }} type="approvers">
        <Box>
          { !isAdvancedEnabled && <FBAutocompleteAsync
            placeholder="form.builder.start.typing.list"
            optionId={FBAutocompleteAsyncOption.availableApprovers}
            optionValueType="id"
            multiple={true}
            withState={true}
            gutter={false}
            omitFormValue={true}
            disabled = {isApproverSelectionDisabled}
            name={`fb-approvers-${name}`}
            {...{ loading, renderOption, defaultValue, autosave, onBlur }}
          /> }
        </Box>
        <Box display="flex" flexDirection="row" flexWrap="wrap">
          <Box display="flex" flexWrap="wrap" mt={1}>
            {approvalApprovedGroup ? (
              approvalApprovedGroup.map((groupName: string, index: number) => (
                <ApprovalApprovedGroup groupName={groupName} key={index} isChecked />
              ))
            ) : (
              <FBMapWrapper<Record<string, FBRequiredApprover>> collection={groups}>
                {(group: FBRequiredApprover, id) => (
                  <Box display="flex" key={`fb-approvers-group-${id}`} mr={2.5}>
                    <ApprovalApprovedGroup
                      groupName={group.name}
                      key={id}
                      isChecked={isIncluded?.(group) || (checkedGroups.findIndex(group => group.id === id) > -1)}
                      dataCyPrefix="fb-approvers"
                    />
                  </Box>
                )}
              </FBMapWrapper>
            )}
            <Box className={isAdvancedEnabled ? classes.switchControlEnabledAr : classes.switchControlDisabledAr}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={isAdvancedEnabled}
                    onChange={(event) => toggleAdvancedConfiguration(event.target.checked)}
                    color="primary"
                    classes={{
                      switchBase: classes.switchBase,
                    }}
                  />
                }
                label="Advanced"
                className={classes.mappingSwitcher}
                labelPlacement="end"
              />
              {isAdvancedEnabled && (
                <Button
                  variant="text"
                  size="small"
                  onClick={configureDialog.open}
                > Configure
                </Button>
              )}
            </Box>
          </Box>
          {!isAdvancedEnabled && <Box flexBasis="100%" >
            {changeRequestApprovals && changeRequestApprovals.length > 0 && (
              <Approvers dataProp={changeRequestApprovals} />
            )}
          </Box> }
          {isAdvancedEnabled && <ApprovalWorkflow
            approvals={changeRequestApprovals}
            currentEmpId={currentEmployeeId}
            workflowStages={approvalStages}
            workflowStatusDetails={workflowStatusDetails}
            changeRequestId={changeRequestId}
          />}
        </Box>
      </FBInput>
      {configureDialog.isOpen && <ApproverConfigurationDialog
        onApprovalStagesUpdated={handleConfirm}
        requiredGroups={groups}
        approvalRequestId={changeRequestId}
        isGetAllExistingConfigurations={true}
        isActionable={isApprovalEditEnabled}
        { ...{
          configureDialog,
          approvalApprovedGroup,
        }}
      />}
      {enableAdvancedDialog?.isOpen && (
        <AlertDialog
          dialog={enableAdvancedDialog}
          confirmAction={onConfirmEnableAdvanced}
          cancelAction={cancelEnableAdvancedDialog}
          message="approvals.switch.to.advanced.msg"
        />
      )}
      {disableAdvancedDialog?.isOpen && (
        <AlertDialog
          dialog={disableAdvancedDialog}
          confirmAction={onConfirmDisableAdvanced}
          cancelAction={cancelDisableAdvancedDialog}
          message="approvals.switch.from.advanced.msg"
        />
      )}
    </>

  );
};

export default withFBApprovers(FBApprovers);
